'use client';

import type { ReactNode } from 'react';
import { useRef, createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { FAVORITES_LOCAL_STORAGE_KEY } from '@libs/core';
import type { FavoriteContextProps, FavoriteStorageData } from '../types';
import { useUserSession } from '../hooks/useUserSession';
import { getFavorites, removeFavorite, addFavorite } from '@libs/api-proxy-client/v2';
import { AccountProduct } from '@libs/common-types';

// caching favorites in localstorage for 6 hours. Fetching favorites again in 6 hours
const CACHE_TIME = 6 * 3600000;

export const FavoritesContext = createContext<FavoriteContextProps | undefined>(undefined);

export function FavoritesProvider({ children }: { children: ReactNode }) {
    const [favorites, setFavorites] = useState<AccountProduct[] | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { user } = useUserSession();
    const isLoadingRef = useRef<boolean>(false);
    const lastUpdatedRef = useRef<number>(0);

    const fetchData = async () => {
        if (isLoadingRef.current) {
            return;
        }
        isLoadingRef.current = true;

        const response = await getFavorites();

        if (response && 'items' in response) {
            setFavorites(response.items);
            lastUpdatedRef.current = Date.now();
        }

        isLoadingRef.current = false;
        setIsLoading(false); //needed due to updateFavorites
    };

    useEffect(() => {
        if (favorites && user) {
            localStorage.setItem(
                FAVORITES_LOCAL_STORAGE_KEY,
                JSON.stringify({
                    customerId: user.customerprofileid,
                    items: favorites,
                    lastUpdated: Date.now(),
                } as FavoriteStorageData)
            );
        }
    }, [favorites, user]);

    useEffect(() => {
        if (user) {
            try {
                const storedFavorites = localStorage.getItem(FAVORITES_LOCAL_STORAGE_KEY);
                const {
                    customerId = 0,
                    items = [],
                    lastUpdated: storageLastUpdated = 0,
                } = JSON.parse(storedFavorites || '{}') as FavoriteStorageData;

                if (
                    customerId !== user.customerprofileid ||
                    !storageLastUpdated ||
                    Date.now() - storageLastUpdated >= CACHE_TIME
                ) {
                    fetchData();
                } else {
                    setFavorites(items);
                    lastUpdatedRef.current = storageLastUpdated;
                }
            } catch (e) {
                console.error('Error parsing local storage favorites:', e);
            }
        }
    }, [user]);

    const removeFromFavorites = useCallback(
        async (productId: string) => {
            if (Array.isArray(favorites)) {
                isLoadingRef.current = true;
                setFavorites(favorites.filter(i => i.sku !== productId));

                const res = await removeFavorite({ sku: productId });

                if (res && 'items' in res) {
                    setFavorites(res.items);
                    lastUpdatedRef.current = Date.now();
                }

                isLoadingRef.current = false;
            }
        },
        [favorites]
    );

    const addToFavorites = useCallback(
        async (productId: string) => {
            if (Array.isArray(favorites)) {
                isLoadingRef.current = true;
                setFavorites([...favorites, { sku: productId } as AccountProduct]);

                const res = await addFavorite({ sku: productId });

                if (res && 'items' in res) {
                    setFavorites(res.items);
                    lastUpdatedRef.current = Date.now();
                }

                isLoadingRef.current = false;
            }
        },
        [favorites]
    );

    const updateFavorites = useCallback(async () => {
        setIsLoading(true);
        return await fetchData();
    }, [favorites]);

    const value = useMemo(() => {
        const handler = {
            /**
             * A Proxy handler for the `favorites` property that fetches updated
             * favorites data from the server if the cached data is stale.
             *
             * @param target - The target object containing the `favorites` property.
             * @param prop - The property being accessed on the target object.
             * @returns The `favorites` property value, or triggers a data fetch if the cached data is stale.
             */
            get: (target: { favorites: AccountProduct[] }, prop: string) => {
                if (
                    prop === 'favorites' &&
                    !isLoadingRef.current &&
                    lastUpdatedRef.current &&
                    Date.now() - lastUpdatedRef.current >= CACHE_TIME
                ) {
                    fetchData();
                }

                return target.favorites;
            },
        };

        return {
            favorites: new Proxy({ favorites }, handler).favorites,
            isLoading,
            addToFavorites,
            removeFromFavorites,
            updateFavorites,
        };
    }, [favorites, isLoading, addToFavorites, removeFromFavorites, updateFavorites]);

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}
